import React from "react";
import { jt, msgid, ngettext, t } from "ttag";

import { constants } from "@reactivated/constants";

import { Form } from "../../../forms/Form";
import { FormNumber } from "../../../forms/FormNumber";
import { FormSelect } from "../../../forms/FormSelect";
import { FormSubmit } from "../../../forms/FormSubmit";
import { isoMonths } from "../../../models/nominals";
import { useFinancingPlans } from "../../../utils/financing";
import { format } from "../../../utils/format";
import { getDinero, getMonthlyPrice } from "../../../utils/money";
import { usePageSetting } from "../../../utils/settings";
import { FinancingModalTrigger } from "../FinancingModalTrigger";

const AppSlug = constants["tsicommon.types.AppSlug"];

const DEFAULT_PAYMENT_AMOUNT = "4000";

const calculatePayment = (paymentAmount: string, paymentTerm: string) => {
    // safeTerm guards prevent SSR page-level crash from `Array(NaN | Infinity)`
    // RangeError in getMonthlyPrice (term is the array length); 360-month cap
    // prevents memory exhaustion. safeAmount is for the integer-truncated
    // display value only — getDinero handles raw strings and decimals safely
    // via its own internal parseFloat, so the monthly calculation passes the
    // unmodified paymentAmount to preserve cents (CSR parity).
    const parsedAmount = parseInt(paymentAmount, 10);
    const safeAmount = Number.isFinite(parsedAmount) ? parsedAmount : 0;
    const parsedTerm = parseInt(paymentTerm, 10);
    const safeTerm =
        Number.isFinite(parsedTerm) && parsedTerm > 0
            ? Math.min(parsedTerm, 360)
            : 1;
    return {
        paymentTotal: format.money(
            getMonthlyPrice(getDinero(paymentAmount), safeTerm),
        ),
        paymentAmountCalcd: String(safeAmount.toFixed(2)),
        paymentTermCalcd: String(safeTerm),
    };
};

export const FinanceCalculator = (): React.JSX.Element | null => {
    const appSlug = usePageSetting("app_slug");
    const disclaimerText =
        appSlug === AppSlug.TEMPURPEDIC || appSlug === AppSlug.SALE2
            ? t`Check table for details about minimum financing requirements`
            : t`See table for details about minimum financing requirements`;

    const plans = useFinancingPlans();

    // Initial state derived synchronously so SSR HTML matches first render
    // (no hydration flash). `plans` is stable per Reactivated page context.
    const [paymentAmount, setPaymentAmount] = React.useState<string>(
        DEFAULT_PAYMENT_AMOUNT,
    );
    const initialTerm =
        plans.length > 0
            ? String(isoMonths.unwrap(plans[plans.length - 1].length))
            : "24";
    const [paymentTerm, setPaymentTerm] = React.useState<string>(initialTerm);
    // CSR parity: payment summary updates only on form submit, not on input change.
    // Grouped into one state so submit fires a single setState; lazy initializer
    // keeps the expensive calculatePayment call to once on mount.
    const [paymentDisplay, setPaymentDisplay] = React.useState(() =>
        calculatePayment(DEFAULT_PAYMENT_AMOUNT, initialTerm),
    );
    const { paymentTotal, paymentAmountCalcd, paymentTermCalcd } =
        paymentDisplay;

    const onCalcPayment = (
        event: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        event.preventDefault();
        setPaymentDisplay(calculatePayment(paymentAmount, paymentTerm));
        return Promise.resolve();
    };

    const onPaymentChange = (event: React.FormEvent<HTMLInputElement>) => {
        setPaymentAmount(event.currentTarget.value);
    };

    const onTermChange = (event: React.FormEvent<HTMLSelectElement>) => {
        setPaymentTerm(event.currentTarget.value);
    };

    return (
        <div className="finance-calculator">
            <h3 className="finance-calculator__title">
                {t`Monthly Payment Calculator`}
            </h3>
            <Form className="finance-calculator-form" onSubmit={onCalcPayment}>
                <fieldset>
                    <legend className="ada-screenreader-only">
                        {t`Calculate Monthly Payment`}
                    </legend>
                    <FormNumber
                        name="finance-payment"
                        label={t`Enter Financed Amount`}
                        id="finance-payment"
                        className="finance-calculator-form__payment"
                        onChange={onPaymentChange}
                        value={paymentAmount}
                        min={1}
                        max={1000000}
                    />
                    <p className="finance-calculator-form__for">{t`for`}</p>
                    <FormSelect
                        name="finance-terms"
                        id="finance-terms"
                        label={t`Term Length Dropdown`}
                        className="finance-calculator-form__terms"
                        value={paymentTerm}
                        onChange={onTermChange}
                        choices={plans.map((plan) => {
                            const planLength = isoMonths.unwrap(plan.length);
                            return {
                                label: ngettext(
                                    msgid`${planLength} Month`,
                                    `${planLength} Months`,
                                    planLength,
                                ),
                                value: `${planLength}`,
                            };
                        })}
                    />
                    <FormSubmit
                        value={t`Calculate Monthly Payment`}
                        className="finance-calculator-form__submit al-financing-page__calculator--calc-payment"
                    />
                </fieldset>
            </Form>
            <div className="finance-calculator__payment">
                {(() => {
                    const monthlyLink = (
                        <a key="monthly" href="#terms_and_conditions">
                            <span className="finance-calculator__payment--underlined">
                                {paymentTotal}
                            </span>
                            <sup>2</sup>
                            <span className="ada-screenreader-only">
                                {t`Additional information about Terms and Conditions`}
                            </span>
                        </a>
                    );
                    const termsLink = (
                        <a key="terms" href="#terms_and_conditions">
                            <span className="finance-calculator__payment--underlined">
                                {paymentTermCalcd}
                            </span>
                            <sup>1</sup>
                            <span className="ada-screenreader-only">
                                {t`Additional information about Terms and Conditions`}
                            </span>
                        </a>
                    );
                    const lineBreak = <br key="br" />;
                    return jt`${monthlyLink}/mo. for ${termsLink} months${lineBreak}(total payments of $${paymentAmountCalcd})`;
                })()}
            </div>
            <p className="finance-calculator__disclaimer">{disclaimerText}</p>
            <FinancingModalTrigger
                modalType="prequal-app"
                className="button button--inverse finance-calculator__button al-financing-page__calculator--check-prequalify"
            >
                {t`Check if you Pre-Qualify`}
            </FinancingModalTrigger>
        </div>
    );
};
